// src/pages/Home.jsx
import React, { useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../AuthProvider";
import { useNavigate } from "react-router-dom";
import Spinner from "../components/Spinner";
import { useTranslation } from "react-i18next";
import { usePermissionsCtx } from "../authz/PermissionsProvider";
import { availableStartPages, pickStartPagePreference, resolveStartRoute } from "../utils/startPagePreference";
import { useStartPageAccess } from "../utils/useStartPageAccess";

const Home = () => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  const authenticated =
    typeof auth?.authenticated === "boolean" ? auth.authenticated : undefined;
  const kcReady = !!auth?.kcReady;
  const iframeTimeout = !!auth?.iframeTimeout;

  // Wacht op permissies uit /authz/me
  const { ready: permsReady } = usePermissionsCtx();

  // Welke startpagina's deze gebruiker kan openen
  const startPageAccess = useStartPageAccess();
  const canChooseStartPage = availableStartPages(startPageAccess).length > 1;
  const subject = auth?.userInfo?.sub || "";
  const [startPreference, setStartPreference] = useState({
    subject: "",
    loaded: false,
    value: null,
  });

  // Eénmalig login proberen
  const triedLoginRef = useRef(false);

  useEffect(() => {
    // Wacht tot Keycloak init klaar is
    if (!kcReady) return;

    // Niet ingelogd? éénmalig login proberen (mits geen iframe-timeout)
    if (authenticated === false && !iframeTimeout && !triedLoginRef.current) {
      triedLoginRef.current = true;
      auth?.keycloak?.login?.({ redirectUri: window.location.href });
      return;
    }

  }, [kcReady, authenticated, iframeTimeout, auth]);

  useEffect(() => {
    if (!authenticated || !permsReady || !subject) return;

    if (!canChooseStartPage) {
      setStartPreference({ subject, loaded: true, value: null });
      return;
    }

    let active = true;
    setStartPreference({ subject, loaded: false, value: null });

    Promise.resolve(auth?.keycloak?.loadUserProfile?.())
      .then((profile) => {
        if (!active) return;
        setStartPreference({
          subject,
          loaded: true,
          value: pickStartPagePreference(profile, auth?.keycloak?.tokenParsed),
        });
      })
      .catch(() => {
        if (active) setStartPreference({ subject, loaded: true, value: null });
      });

    return () => {
      active = false;
    };
  }, [authenticated, permsReady, subject, canChooseStartPage, auth?.keycloak]);

  useEffect(() => {
    if (!authenticated || !permsReady || !subject) return;
    if (!startPreference.loaded || startPreference.subject !== subject) return;

    navigate(resolveStartRoute({
      ...startPageAccess,
      preference: startPreference.value,
    }), { replace: true });
  }, [
    authenticated,
    permsReady,
    subject,
    startPreference,
    startPageAccess,
    navigate,
  ]);

  // Loader tonen zolang KC init loopt of permissies nog laden na succesvolle auth
  const showLoading =
    (!kcReady && !iframeTimeout) ||
    (authenticated && (!permsReady || !startPreference.loaded || startPreference.subject !== subject));

  if (showLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-900 p-6">
        <Spinner />
        <p className="mt-4 text-gray-600 dark:text-gray-300 text-lg font-medium">
          {t("home.loading_authentication", "Bezig met doorsturen…")}
        </p>
      </div>
    );
  }

  // Navigatie gebeurt in useEffect
  return <div className="min-h-screen" />;
};

export default Home;
